import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

// Define the Authentication context type
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

// User type definition
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  login: async () => false,
  logout: () => {},
  loading: true
});

// Props type for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

// The Authentication Provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Load authentication state from localStorage on initial mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (token && userData) {
          // In a real app, you might want to validate the token with the server here
          setIsAuthenticated(true);
          setUser(userData as unknown as User);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Clear any potentially corrupted data
        // localStorage.removeItem('token');
        // localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      // In a real app, you would make an API call here
      // For demo purposes, we'll simulate a successful login with mock data
      const response = await simulateApiCall({ email, password });
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        setIsAuthenticated(true);
        setUser(response.user as User);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  // Simulated API call for demonstration purposes
  const simulateApiCall = async (credentials: { email: string, password: string }) => {
    // This is a mock implementation - replace with actual API call
    await new Promise(resolve => setTimeout(resolve, 800)); // Simulate network delay
    
    // For demo purposes, accept any email with a password containing at least 6 characters
    if (credentials.password.length >= 6) {
      return {
        token: 'mock-jwt-token',
        user: {
          id: '1',
          name: 'Demo User',
          email: credentials.email,
          role: 'admin'
        }
      };
    }
    
    throw new Error('Invalid credentials');
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);
